/**
 * Eligibility Wizard for the Big Bad Cough Settlement website.
 *
 * @module eligibility
 * @author Big Bad Cough Settlement Team
 * @version 1.0.0
 * @description Interactive eligibility wizard to determine class member status and payment tier.
 *
 * Business Logic:
 * - Eligible Class Members: Adults who viewed the Advertising Campaign, lived in NY or were
 *   vaccinated in NY, vaccinated with BOOSTRIX during Class Period to protect others from pertussis.
 * - Exclusions: Pregnant when vaccinated, GSK affiliates, presiding judges, party counsel, opt-outs.
 * - Payment Tiers: $10 (attestation only) or $50 (attestation + qualifying proof).
 */

import { userLanguage } from "commonAssets/js/constants";
import { createElement } from "commonAssets/lib/createElement";

/**
 * i18n content for the eligibility wizard
 */
const translations = {
    en: {
        title: "Check Your Eligibility",
        subtitle:
            "Click on the Get Started button below to see if you may be eligible to file a claim for this settlement.",
        questions: {
            age: {
                question: "Are you 18 years of age or older?",
                yes: "Yes",
                no: "No",
            },
            viewedAd: {
                question: "Did you view the BOOSTRIX Advertising Campaign?",
                yes: "Yes",
                no: "No",
            },
            nyConnection: {
                question:
                    "Did you live in New York at the time of vaccination, OR were you vaccinated with BOOSTRIX in New York?",
                yes: "Yes",
                no: "No",
            },
            vaccinatedClassPeriod: {
                question:
                    "Were you vaccinated with BOOSTRIX between May 20, 2016 and May 20, 2020?",
                yes: "Yes",
                no: "No",
            },
            protectOthers: {
                question:
                    "Was your vaccination to protect one or more other individuals from pertussis (whooping cough)?",
                yes: "Yes",
                no: "No",
            },
            pregnantExclusion: {
                question:
                    "Were you pregnant at the time you were vaccinated with BOOSTRIX?",
                yes: "Yes",
                no: "No",
            },
        },
        results: {
            eligible: {
                title: "You May Be Eligible",
                message:
                    "Based on your responses, you may be eligible for this settlement.",
                paymentTiers: {
                    intro: "There are two payment tiers based on documentation:",
                    tier1: {
                        amount: "$10.00",
                        description:
                            "Submit a Claim Form with Attestation of Vaccination (no proof required)",
                    },
                    tier2: {
                        amount: "$50.00",
                        description:
                            "Submit a Claim Form with Attestation AND Qualifying Proof of Vaccination",
                    },
                },
                cta: "Submit a Claim",
                ctaLink: "/submit-claim",
            },
            ineligible: {
                title: "You May Not Be Eligible",
                message:
                    "Based on your responses, you may not be eligible for this settlement.",
                reasons: {
                    age: "You must be 18 years of age or older.",
                    viewedAd:
                        "You must have viewed the BOOSTRIX Advertising Campaign.",
                    nyConnection:
                        "You must have lived in New York or been vaccinated in New York.",
                    vaccinatedClassPeriod:
                        "You must have been vaccinated with BOOSTRIX between May 20, 2016 and May 20, 2020.",
                    protectOthers:
                        "Your vaccination must have been to protect others from pertussis.",
                    pregnantExclusion:
                        "Women who were vaccinated while pregnant are excluded from this settlement.",
                },
                cta: "Learn More",
                ctaLink: "/faqs",
            },
        },
        buttons: {
            getStarted: "Get Started",
            back: "Back",
            startOver: "Start Over",
        },
        classDefinition: {
            intro: "The Settlement Class is defined as:",
            // Parts of the class definition - emphasis key indicates which part to bold
            parts: {
                prefix: "All ",
                age: "adults",
                midAge: " who ",
                viewedAd: "viewed the Advertising Campaign",
                midViewedAd: ", and ",
                nyConnection:
                    "either lived in New York at the time or were vaccinated with BOOSTRIX in New York",
                midNyConnection: ", and were ",
                vaccinatedClassPeriod:
                    "vaccinated with BOOSTRIX during the Class Period",
                midVaccinatedClassPeriod: " ",
                protectOthers:
                    "in order to protect one or more other individuals from pertussis",
                suffix: ".",
            },
            exclusion:
                "Excluded from the Settlement Class are: (i) any woman who was vaccinated with BOOSTRIX while pregnant; (ii) GSK, its officers, directors, affiliates, legal representatives, employees, successors, and assigns, and entities in which GSK has a controlling interest; (iii) judges presiding over the Litigation; (iv) counsel of record for the Parties; and (v) all Persons who validly opt-out in a timely manner.",
        },
        proofFlow: {
            initialQuestion: "Do you have Qualifying Proof of Vaccination?",
            yes: "Yes",
            no: "No",
            questions: {
                healthcareProvider: {
                    question:
                        "Do you have documentation created by a health care provider or pharmacy that administered the vaccination that shows you were vaccinated with BOOSTRIX between May 20, 2016 and May 20, 2020?",
                },
                insuranceProvider: {
                    question:
                        "Do you have a record created by your insurance provider that shows you were vaccinated with BOOSTRIX between May 20, 2016 and May 20, 2020?",
                },
                otherMedicalRecords: {
                    question:
                        "Do you have any other medical records created before February 10, 2025 that show you were vaccinated with BOOSTRIX between May 20, 2016 and May 20, 2020? Please be aware that these records must be prepared by a third party, such as medical records maintained by a hospital or a primary care physician.",
                },
            },
            results: {
                tier50: {
                    message:
                        "Based on your responses, you may be eligible for the $50.00 payment tier.",
                    cta: "Submit a Claim",
                    ctaLink: "/submit-claim",
                },
                tier10: {
                    message:
                        "Without Qualifying Proof of Vaccination, you are not eligible for the $50.00 payment tier. To file a claim for $10.00 without Qualifying Proof of Vaccination, click on the Submit Claim button below.",
                    cta: "Submit a Claim",
                    ctaLink: "/submit-claim",
                },
            },
        },
    },
    es: {
        title: "Verifique Su Elegibilidad",
        subtitle:
            "Haga clic en el botón Comenzar a continuación para ver si puede ser elegible para presentar un reclamo en este acuerdo.",
        questions: {
            age: {
                question: "¿Tiene 18 años de edad o más?",
                yes: "Sí",
                no: "No",
            },
            viewedAd: {
                question: "¿Vio la Campaña Publicitaria de BOOSTRIX?",
                yes: "Sí",
                no: "No",
            },
            nyConnection: {
                question:
                    "¿Vivía en Nueva York en el momento de la vacunación, O fue vacunado con BOOSTRIX en Nueva York?",
                yes: "Sí",
                no: "No",
            },
            vaccinatedClassPeriod: {
                question:
                    "¿Fue vacunado con BOOSTRIX entre el 20 de mayo de 2016 y el 20 de mayo de 2020?",
                yes: "Sí",
                no: "No",
            },
            protectOthers: {
                question:
                    "¿Su vacunación fue para proteger a una o más personas de la tos ferina (pertussis)?",
                yes: "Sí",
                no: "No",
            },
            pregnantExclusion: {
                question:
                    "¿Estaba embarazada cuando fue vacunada con BOOSTRIX?",
                yes: "Sí",
                no: "No",
            },
        },
        results: {
            eligible: {
                title: "Puede Ser Elegible",
                message:
                    "Según sus respuestas, puede ser elegible para este acuerdo.",
                paymentTiers: {
                    intro: "Hay dos niveles de pago según la documentación:",
                    tier1: {
                        amount: "$10.00",
                        description:
                            "Envíe un Formulario de Reclamo con Declaración de Vacunación (no se requiere prueba)",
                    },
                    tier2: {
                        amount: "$50.00",
                        description:
                            "Envíe un Formulario de Reclamo con Declaración Y Prueba Calificada de Vacunación",
                    },
                },
                cta: "Enviar un Reclamo",
                ctaLink: "/submit-claim",
            },
            ineligible: {
                title: "Puede No Ser Elegible",
                message:
                    "Según sus respuestas, puede no ser elegible para este acuerdo.",
                reasons: {
                    age: "Debe tener 18 años de edad o más.",
                    viewedAd:
                        "Debe haber visto la Campaña Publicitaria de BOOSTRIX.",
                    nyConnection:
                        "Debe haber vivido en Nueva York o haber sido vacunado en Nueva York.",
                    vaccinatedClassPeriod:
                        "Debe haber sido vacunado con BOOSTRIX entre el 20 de mayo de 2016 y el 20 de mayo de 2020.",
                    protectOthers:
                        "Su vacunación debe haber sido para proteger a otros de la tos ferina.",
                    pregnantExclusion:
                        "Las mujeres que fueron vacunadas mientras estaban embarazadas están excluidas de este acuerdo.",
                },
                cta: "Más Información",
                ctaLink: "/faqs",
            },
        },
        buttons: {
            getStarted: "Comenzar",
            back: "Atrás",
            startOver: "Comenzar de Nuevo",
        },
        classDefinition: {
            intro: "La Clase del Acuerdo se define como:",
            parts: {
                prefix: "Todos los ",
                age: "adultos",
                midAge: " que ",
                viewedAd: "vieron la Campaña Publicitaria",
                midViewedAd: ", y ",
                nyConnection:
                    "vivían en Nueva York en ese momento o fueron vacunados con BOOSTRIX en Nueva York",
                midNyConnection: ", y fueron ",
                vaccinatedClassPeriod:
                    "vacunados con BOOSTRIX durante el Período de Clase",
                midVaccinatedClassPeriod: " ",
                protectOthers:
                    "para proteger a una o más personas de la tos ferina",
                suffix: ".",
            },
            exclusion:
                "Se excluyen de la Clase del Acuerdo: (i) cualquier mujer que fue vacunada con BOOSTRIX mientras estaba embarazada; (ii) GSK, sus funcionarios, directores, afiliados, representantes legales, empleados, sucesores y cesionarios, y entidades en las que GSK tiene un interés de control; (iii) jueces que presiden el Litigio; (iv) abogados de registro de las Partes; y (v) todas las Personas que válidamente opten por no participar de manera oportuna.",
        },
        proofFlow: {
            initialQuestion: "¿Tiene Prueba Calificada de Vacunación?",
            yes: "Sí",
            no: "No",
            questions: {
                healthcareProvider: {
                    question:
                        "¿Tiene documentación creada por un proveedor de atención médica o farmacia que administró la vacuna que muestre que fue vacunado con BOOSTRIX entre el 20 de mayo de 2016 y el 20 de mayo de 2020?",
                },
                insuranceProvider: {
                    question:
                        "¿Tiene un registro creado por su proveedor de seguros que muestre que fue vacunado con BOOSTRIX entre el 20 de mayo de 2016 y el 20 de mayo de 2020?",
                },
                otherMedicalRecords: {
                    question:
                        "¿Tiene otros registros médicos creados antes del 10 de febrero de 2025 que muestren que fue vacunado con BOOSTRIX entre el 20 de mayo de 2016 y el 20 de mayo de 2020? Tenga en cuenta que estos registros deben ser preparados por un tercero, como registros médicos mantenidos por un hospital o un médico de atención primaria.",
                },
            },
            results: {
                tier50: {
                    message:
                        "Según sus respuestas, puede ser elegible para el nivel de pago de $50.00.",
                    cta: "Enviar un Reclamo",
                    ctaLink: "/submit-claim",
                },
                tier10: {
                    message:
                        "Sin Prueba Calificada de Vacunación, no es elegible para el nivel de pago de $50.00. Para presentar un reclamo por $10.00 sin Prueba Calificada de Vacunación, haga clic en el botón Enviar Reclamo a continuación.",
                    cta: "Enviar un Reclamo",
                    ctaLink: "/submit-claim",
                },
            },
        },
    },
};

// Get content for current language, fallback to English
const getEligibilityContent = () =>
    translations[userLanguage] || translations.en;

/**
 * Decision tree configuration for the eligibility wizard.
 * Each step defines the question key and what happens on yes/no.
 */
const decisionTree = [
    { key: "age", onNo: "ineligible" },
    { key: "viewedAd", onNo: "ineligible" },
    { key: "nyConnection", onNo: "ineligible" },
    { key: "vaccinatedClassPeriod", onNo: "ineligible" },
    { key: "protectOthers", onNo: "ineligible" },
    { key: "pregnantExclusion", onYes: "ineligible", onNo: "eligible" }, // Inverted: Yes = excluded
];

/**
 * Proof of vaccination decision tree.
 * Each step defines the question key; Yes = tier50, No = next question or tier10.
 */
const proofTree = [
    { key: "healthcareProvider", onYes: "tier50" },
    { key: "insuranceProvider", onYes: "tier50" },
    { key: "otherMedicalRecords", onYes: "tier50", onNo: "tier10" },
];

/**
 * EligibilityWizard class manages the interactive eligibility determination.
 */
class EligibilityWizard {
    constructor(container, content) {
        this.container = container;
        this.content = content;
        this.currentStep = 0;
        this.answers = {};
        this.ineligibleReason = null;
        // Proof flow state
        this.proofStep = -1; // -1 = initial question, 0+ = proof tree questions
        this.selectedTier = null;
    }

    /**
     * Initialize and render the wizard
     */
    init() {
        this.renderIntro();
    }

    /**
     * Render the introduction screen
     */
    renderIntro() {
        this.container.innerHTML = "";

        const intro = createElement(
            "div",
            { class: "eligibility-wizard eligibility-intro" },
            [
                createElement(
                    "h2",
                    { class: "wizard-title" },
                    [],
                    this.content.title
                ),
                createElement(
                    "p",
                    { class: "wizard-subtitle" },
                    [],
                    this.content.subtitle
                ),
                createElement(
                    "button",
                    { class: "btn btn-primary btn-start", type: "button" },
                    [],
                    this.content.buttons.getStarted,
                    { click: () => this.renderQuestion() }
                ),
            ]
        );

        this.container.appendChild(intro);
        this.fadeIn(intro);
    }

    /**
     * Render the current question step
     */
    renderQuestion() {
        const step = decisionTree[this.currentStep];
        const questionData = this.content.questions[step.key];

        this.container.innerHTML = "";

        const questionEl = createElement(
            "div",
            {
                class: "eligibility-wizard eligibility-question",
                "data-step": this.currentStep,
            },
            [
                createElement(
                    "h3",
                    { class: "question-text" },
                    [],
                    questionData.question
                ),
                createElement("div", { class: "question-buttons" }, [
                    createElement(
                        "button",
                        {
                            class: "btn btn-outline-primary btn-answer",
                            "data-answer": "yes",
                            type: "button",
                        },
                        [],
                        questionData.yes,
                        { click: () => this.handleAnswer("yes") }
                    ),
                    createElement(
                        "button",
                        {
                            class: "btn btn-outline-primary btn-answer",
                            "data-answer": "no",
                            type: "button",
                        },
                        [],
                        questionData.no,
                        { click: () => this.handleAnswer("no") }
                    ),
                ]),
                this.currentStep > 0
                    ? createElement(
                          "button",
                          { class: "btn btn-link btn-back", type: "button" },
                          [],
                          this.content.buttons.back,
                          { click: () => this.goBack() }
                      )
                    : createElement("span"),
            ]
        );

        this.container.appendChild(questionEl);
        this.fadeIn(questionEl);
    }

    /**
     * Handle user's answer and determine next step
     */
    handleAnswer(answer) {
        const step = decisionTree[this.currentStep];
        this.answers[step.key] = answer;

        // Check for ineligibility
        if (
            (answer === "yes" && step.onYes === "ineligible") ||
            (answer === "no" && step.onNo === "ineligible")
        ) {
            this.ineligibleReason = step.key;
            this.renderResult("ineligible");
            return;
        }

        // Check for eligibility (end of tree)
        if (
            (answer === "yes" && step.onYes === "eligible") ||
            (answer === "no" && step.onNo === "eligible")
        ) {
            this.renderResult("eligible");
            return;
        }

        // Move to next question
        this.currentStep++;
        this.renderQuestion();
    }

    /**
     * Go back to previous question
     */
    goBack() {
        if (this.currentStep > 0) {
            this.currentStep--;
            this.renderQuestion();
        }
    }

    /**
     * Render the result screen (eligible or ineligible)
     */
    renderResult(status) {
        const resultData = this.content.results[status];
        this.container.innerHTML = "";

        let resultContent = [
            createElement(
                "h2",
                { class: `result-title result-${status}` },
                [],
                resultData.title
            ),
            createElement(
                "p",
                { class: "result-message" },
                [],
                resultData.message
            ),
        ];

        if (status === "eligible") {
            // Add payment tier information
            resultContent.push(
                createElement("div", { class: "payment-tiers" }, [
                    createElement(
                        "p",
                        { class: "tiers-intro" },
                        [],
                        resultData.paymentTiers.intro
                    ),
                    createElement("div", { class: "tier tier-1" }, [
                        createElement(
                            "span",
                            { class: "tier-amount" },
                            [],
                            resultData.paymentTiers.tier1.amount
                        ),
                        createElement(
                            "p",
                            { class: "tier-description" },
                            [],
                            resultData.paymentTiers.tier1.description
                        ),
                    ]),
                    createElement("div", { class: "tier tier-2" }, [
                        createElement(
                            "span",
                            { class: "tier-amount" },
                            [],
                            resultData.paymentTiers.tier2.amount
                        ),
                        createElement(
                            "p",
                            { class: "tier-description" },
                            [],
                            resultData.paymentTiers.tier2.description
                        ),
                    ]),
                ])
            );

            // Add proof flow question if proofFlow content exists
            if (this.content.proofFlow) {
                const proofFlow = this.content.proofFlow;
                resultContent.push(
                    createElement("div", { class: "proof-flow-question mt-4" }, [
                        createElement(
                            "h4",
                            { class: "proof-question-text" },
                            [],
                            proofFlow.initialQuestion
                        ),
                        createElement("div", { class: "question-buttons mt-3" }, [
                            createElement(
                                "button",
                                {
                                    class: "btn btn-outline-primary btn-answer",
                                    "data-answer": "yes",
                                    type: "button",
                                },
                                [],
                                proofFlow.yes,
                                { click: () => this.handleProofInitialAnswer("yes") }
                            ),
                            createElement(
                                "button",
                                {
                                    class: "btn btn-outline-primary btn-answer",
                                    "data-answer": "no",
                                    type: "button",
                                },
                                [],
                                proofFlow.no,
                                { click: () => this.handleProofInitialAnswer("no") }
                            ),
                        ]),
                    ])
                );

                // Add start over button (no direct submit CTA)
                resultContent.push(
                    createElement("div", { class: "result-actions mt-3" }, [
                        createElement(
                            "button",
                            { class: "btn btn-link btn-start-over", type: "button" },
                            [],
                            this.content.buttons.startOver,
                            { click: () => this.startOver() }
                        ),
                    ])
                );

                const resultEl = createElement(
                    "div",
                    {
                        class: `eligibility-wizard eligibility-result result-${status}`,
                    },
                    resultContent
                );

                this.container.appendChild(resultEl);
                this.fadeIn(resultEl);
                return; // Exit early - proof flow handles its own CTA
            }
        } else {
            // Add ineligibility reason
            const reason =
                this.content.results.ineligible.reasons[this.ineligibleReason];
            if (reason) {
                resultContent.push(
                    createElement(
                        "p",
                        { class: "ineligible-reason" },
                        [],
                        reason
                    )
                );
            }

            // Add class definition box with emphasized relevant portion
            if (this.ineligibleReason && this.content.classDefinition) {
                resultContent.push(
                    this.buildClassDefinition(this.ineligibleReason)
                );
            }
        }

        // Add CTA button
        resultContent.push(
            createElement("div", { class: "result-actions" }, [
                createElement(
                    "a",
                    { class: "btn btn-primary", href: resultData.ctaLink },
                    [],
                    resultData.cta
                ),
                createElement(
                    "button",
                    { class: "btn btn-link btn-start-over", type: "button" },
                    [],
                    this.content.buttons.startOver,
                    { click: () => this.startOver() }
                ),
            ])
        );

        const resultEl = createElement(
            "div",
            { class: `eligibility-wizard eligibility-result result-${status}` },
            resultContent
        );

        this.container.appendChild(resultEl);
        this.fadeIn(resultEl);
    }

    /**
     * Reset wizard to beginning
     */
    startOver() {
        this.currentStep = 0;
        this.answers = {};
        this.ineligibleReason = null;
        this.proofStep = -1;
        this.selectedTier = null;
        this.renderIntro();
    }

    /**
     * Fade in animation helper
     */
    fadeIn(element) {
        element.classList.add("fade-in");
        requestAnimationFrame(() => {
            element.classList.add("visible");
        });
    }

    /**
     * Build the class definition element with emphasis on the relevant portion
     * @param {string} emphasisKey - The key indicating which part to emphasize
     * @returns {HTMLElement} The class definition container element
     */
    buildClassDefinition(emphasisKey) {
        const classDef = this.content.classDefinition;
        const parts = classDef.parts;

        // For pregnancy exclusion, show exclusion text instead
        if (emphasisKey === "pregnantExclusion") {
            return createElement(
                "div",
                {
                    class: "class-definition-box bg-body-tertiary p-3 rounded-3 mt-3",
                },
                [
                    createElement(
                        "p",
                        { class: "class-definition-text mb-0" },
                        [],
                        classDef.exclusion
                    ),
                ]
            );
        }

        // Build the class definition with emphasis
        const textContainer = createElement("p", {
            class: "class-definition-text mb-0",
        });

        // Helper to append text or emphasized text
        const appendText = (text, isEmphasized = false) => {
            if (isEmphasized) {
                const strong = document.createElement("strong");
                strong.textContent = text;
                textContainer.appendChild(strong);
            } else {
                textContainer.appendChild(document.createTextNode(text));
            }
        };

        // Build the sentence with appropriate emphasis
        appendText(parts.prefix);
        appendText(parts.age, emphasisKey === "age");
        appendText(parts.midAge);
        appendText(parts.viewedAd, emphasisKey === "viewedAd");
        appendText(parts.midViewedAd);
        appendText(parts.nyConnection, emphasisKey === "nyConnection");
        appendText(parts.midNyConnection);
        appendText(
            parts.vaccinatedClassPeriod,
            emphasisKey === "vaccinatedClassPeriod"
        );
        appendText(parts.midVaccinatedClassPeriod);
        appendText(parts.protectOthers, emphasisKey === "protectOthers");
        appendText(parts.suffix);

        return createElement(
            "div",
            {
                class: "class-definition-box bg-body-tertiary p-3 rounded-3 mt-3",
            },
            [
                createElement(
                    "p",
                    { class: "class-definition-intro fw-semibold mb-2" },
                    [],
                    classDef.intro
                ),
                textContainer,
            ]
        );
    }

    /**
     * Handle initial proof question answer (Do you have Qualifying Proof?)
     * @param {string} answer - "yes" or "no"
     */
    handleProofInitialAnswer(answer) {
        if (answer === "no") {
            // No proof → tier 10
            this.selectedTier = "tier10";
            this.renderTierResult();
        } else {
            // Yes → start proof tree questions
            this.proofStep = 0;
            this.renderProofQuestion();
        }
    }

    /**
     * Render a proof tree question
     */
    renderProofQuestion() {
        const step = proofTree[this.proofStep];
        const questionData = this.content.proofFlow.questions[step.key];
        const proofFlow = this.content.proofFlow;

        this.container.innerHTML = "";

        const questionEl = createElement(
            "div",
            { class: "eligibility-wizard eligibility-proof-question" },
            [
                createElement(
                    "h3",
                    { class: "question-text" },
                    [],
                    questionData.question
                ),
                createElement("div", { class: "question-buttons mt-3" }, [
                    createElement(
                        "button",
                        {
                            class: "btn btn-outline-primary btn-answer",
                            "data-answer": "yes",
                            type: "button",
                        },
                        [],
                        proofFlow.yes,
                        { click: () => this.handleProofAnswer("yes") }
                    ),
                    createElement(
                        "button",
                        {
                            class: "btn btn-outline-primary btn-answer",
                            "data-answer": "no",
                            type: "button",
                        },
                        [],
                        proofFlow.no,
                        { click: () => this.handleProofAnswer("no") }
                    ),
                ]),
                createElement(
                    "button",
                    { class: "btn btn-link btn-back mt-3", type: "button" },
                    [],
                    this.content.buttons.back,
                    { click: () => this.goBackInProofFlow() }
                ),
            ]
        );

        this.container.appendChild(questionEl);
        this.fadeIn(questionEl);
    }

    /**
     * Handle proof tree question answer
     * @param {string} answer - "yes" or "no"
     */
    handleProofAnswer(answer) {
        const step = proofTree[this.proofStep];

        if (answer === "yes" && step.onYes === "tier50") {
            this.selectedTier = "tier50";
            this.renderTierResult();
            return;
        }

        if (answer === "no") {
            if (step.onNo === "tier10") {
                this.selectedTier = "tier10";
                this.renderTierResult();
                return;
            }
            // Move to next proof question
            this.proofStep++;
            this.renderProofQuestion();
        }
    }

    /**
     * Go back in proof flow
     */
    goBackInProofFlow() {
        if (this.proofStep > 0) {
            this.proofStep--;
            this.renderProofQuestion();
        } else {
            // Go back to eligible result with proof question
            this.proofStep = -1;
            this.renderResult("eligible");
        }
    }

    /**
     * Render the final tier result with CTA
     */
    renderTierResult() {
        const tierData = this.content.proofFlow.results[this.selectedTier];
        this.container.innerHTML = "";

        const resultEl = createElement(
            "div",
            { class: "eligibility-wizard eligibility-tier-result" },
            [
                createElement(
                    "h2",
                    { class: "result-title result-eligible" },
                    [],
                    this.content.results.eligible.title
                ),
                createElement(
                    "p",
                    { class: "result-message" },
                    [],
                    tierData.message
                ),
                createElement("div", { class: "result-actions mt-4" }, [
                    createElement(
                        "a",
                        { class: "btn btn-primary", href: tierData.ctaLink },
                        [],
                        tierData.cta
                    ),
                    createElement(
                        "button",
                        { class: "btn btn-link btn-start-over", type: "button" },
                        [],
                        this.content.buttons.startOver,
                        { click: () => this.startOver() }
                    ),
                ]),
            ]
        );

        this.container.appendChild(resultEl);
        this.fadeIn(resultEl);
    }
}

/**
 * Initialize the eligibility wizard on the page
 */
export function eligibility() {
    const container = document.getElementById("eligibility-wizard-container");

    if (!container) {
        // Create container if it doesn't exist
        const main = document.querySelector("main .container");
        if (main) {
            const wizardContainer = createElement("div", {
                id: "eligibility-wizard-container",
                class: "mt-4",
            });
            main.appendChild(wizardContainer);
            const wizard = new EligibilityWizard(
                wizardContainer,
                getEligibilityContent()
            );
            wizard.init();
            insertEligibilityImage(wizardContainer);
        } else {
            console.warn("Eligibility: Could not find main container");
        }
        return;
    }

    const wizard = new EligibilityWizard(container, getEligibilityContent());
    wizard.init();
    insertEligibilityImage(container);
}

/**
 * Inserts the case image centered below the eligibility wizard container.
 * @param {HTMLElement} wizardContainer - The #eligibility-wizard-container element.
 */
function insertEligibilityImage(wizardContainer) {
    if (document.getElementById("eligibility-case-image")) return;

    const row = createElement("div", {
        class: "text-center my-4",
        id: "eligibility-case-image",
    });

    const img = document.createElement("img");
    img.src =
        "https://angeion-public.s3.us-east-1.amazonaws.com/www.bigbadcoughsettlement.com/assets/img/bigbadcoughsettlement.com_000_img.png";
    img.alt =
        userLanguage === "es"
            ? "Imagen del acuerdo DeCostanzo v. GlaxoSmithKline"
            : "DeCostanzo v. GlaxoSmithKline settlement image";
    img.className = "img-fluid";
    img.style.maxHeight = "300px";
    img.style.objectFit = "contain";

    row.appendChild(img);
    wizardContainer.insertAdjacentElement("afterend", row);
}
